import React, { useMemo, useState } from 'react';
import {
	Alert,
	Autocomplete,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Grid,
	InputAdornment,
	Switch,
	TextField,
} from '@mui/material';
import { IProviderListDTO } from 'features/providers/types';
import { createCostIssuingRule, updateCostIssuingRule } from '../../services';
import { IssueCostCondition, IssueCostRuleForm } from '../../types';
import {
	EMPTY_RULE_FORM,
	IssueCostRuleFormErrors,
	describeRuleWriteError,
	formToUpsert,
	isRuleGone,
	isSameRule,
	ruleToForm,
	validateRuleForm,
} from '../../helpers';

interface Props {
	/** La regla a editar (con id); sin ella, es un alta. */
	rule: IssueCostCondition | null;
	providers: IProviderListDTO[];
	onClose: () => void;
	onSaved: (message: string) => void;
	/** La regla que se editaba ya no existe (otro la borró): la lista tiene que actualizarse. */
	onGone: (message: string) => void;
}

/**
 * Alta o edición de una regla de costo de emisión. Se monta al abrirse (el estado sale de `rule`, sin pasar por los
 * datos de la vez anterior). Los % se cargan enteros: la base guarda 2 decimales de la fracción.
 */
export function CostIssuingRuleDialog({ rule, providers, onClose, onSaved, onGone }: Props): JSX.Element {
	const [form, setForm] = useState<IssueCostRuleForm>(() => (rule ? ruleToForm(rule) : EMPTY_RULE_FORM));
	const [errors, setErrors] = useState<IssueCostRuleFormErrors>({});
	const [saving, setSaving] = useState(false);
	const [apiError, setApiError] = useState<string | null>(null);
	const editingId = rule?.id;

	const selectedProvider = useMemo(
		() => providers.find((provider) => provider.id === form.providerId) ?? null,
		[providers, form.providerId],
	);

	const set = (field: keyof IssueCostRuleForm) => (event: React.ChangeEvent<HTMLInputElement>) =>
		setForm((current) => ({ ...current, [field]: event.target.value }));

	const handleSave = async () => {
		const found = validateRuleForm(form);
		setErrors(found);
		if (Object.keys(found).length > 0) return;
		const upsert = formToUpsert(form);
		// Guardar sin cambios no llama a la API: un UPDATE igual mueve la fila en la tabla y puede cambiar el tipo de
		// tarifa que la calculadora preselecciona (toma el primero que recibe).
		if (rule && editingId !== undefined && isSameRule(upsert, formToUpsert(ruleToForm(rule)))) {
			onClose();
			return;
		}
		setSaving(true);
		setApiError(null);
		try {
			if (editingId !== undefined) await updateCostIssuingRule(editingId, upsert);
			else await createCostIssuingRule(upsert);
			onSaved(editingId !== undefined ? 'Regla actualizada' : 'Regla creada');
		} catch (error) {
			const message = describeRuleWriteError(error, 'No se pudo guardar la regla');
			if (isRuleGone(error)) onGone(message);
			else {
				setApiError(message);
				setSaving(false);
			}
		}
	};

	const percentField = (field: keyof IssueCostRuleForm, label: string) => (
		<TextField
			fullWidth
			size="small"
			label={label}
			type="number"
			inputProps={{ min: 0, max: 100, step: 1 }}
			InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
			value={form[field]}
			onChange={set(field)}
			error={Boolean(errors[field])}
			helperText={errors[field]}
		/>
	);

	return (
		<Dialog open onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				{editingId !== undefined ? 'Editar regla de costo de emisión' : 'Nueva regla de costo de emisión'}
			</DialogTitle>
			<DialogContent>
				<Grid container spacing={2} sx={{ mt: 0 }}>
					<Grid item xs={12}>
						<Autocomplete
							options={providers}
							value={selectedProvider}
							getOptionLabel={(option) => option.fantasyName}
							isOptionEqualToValue={(option, value) => option.id === value.id}
							onChange={(_, value) => setForm((current) => ({ ...current, providerId: value?.id ?? '' }))}
							noOptionsText="No hay proveedores con ese nombre"
							disabled={saving}
							renderInput={(params) => (
								<TextField
									{...params}
									size="small"
									label="Proveedor"
									error={Boolean(errors.providerId)}
									helperText={errors.providerId}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							fullWidth
							size="small"
							label="Validating carrier"
							placeholder="LA, AR… o REST"
							value={form.validatingCarrier}
							onChange={set('validatingCarrier')}
							error={Boolean(errors.validatingCarrier)}
							helperText={errors.validatingCarrier ?? 'REST: las aerolíneas sin regla propia'}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							fullWidth
							size="small"
							label="Tipo de tarifa"
							placeholder="TODAS"
							value={form.tariffType}
							onChange={set('tariffType')}
							error={Boolean(errors.tariffType)}
							helperText={errors.tariffType}
						/>
					</Grid>
					<Grid item xs={12} sm={4}>
						{percentField('commissionPercent', 'Comisión')}
					</Grid>
					<Grid item xs={12} sm={4}>
						{percentField('overPercent', 'Over')}
					</Grid>
					<Grid item xs={12} sm={4}>
						{percentField('feePercent', 'Fee')}
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							fullWidth
							size="small"
							label={form.applyMinimum ? 'Fee mínimo' : 'Fee fijo'}
							type="number"
							inputProps={{ min: 0, step: 0.01 }}
							InputProps={{ startAdornment: <InputAdornment position="start">USD</InputAdornment> }}
							value={form.feeValue}
							onChange={set('feeValue')}
							error={Boolean(errors.feeValue)}
							helperText={
								errors.feeValue ??
								(form.applyMinimum ? 'Se cobra el mayor entre el % y este mínimo' : '0 = se cobra el % de fee')
							}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<FormControlLabel
							control={
								<Switch
									checked={form.applyMinimum}
									onChange={(event) => setForm((current) => ({ ...current, applyMinimum: event.target.checked }))}
								/>
							}
							label="Aplica mínimo"
						/>
					</Grid>
					{apiError && (
						<Grid item xs={12}>
							<Alert severity="error">{apiError}</Alert>
						</Grid>
					)}
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={saving}>
					Cancelar
				</Button>
				<Button
					onClick={handleSave}
					variant="contained"
					disabled={saving}
					startIcon={saving ? <CircularProgress size={16} /> : undefined}
				>
					Guardar
				</Button>
			</DialogActions>
		</Dialog>
	);
}
